import { sortBy } from 'lodash';
import { newLogoReachOk64 } from 'reports/base64Images';
import { groupBy } from 'selectors/generic';
import store from 'source/store';
import theme from 'source/theme';
import { F, F_ID, IS, IS_ID, lcsNames, M, M_ID } from 'utils/constants/lcsTypes';
import { shortDate } from 'utils/helperFunctions/dateFormat';

// `flow.steps` and `step.substeps` - both are the steps slice, read by a
// different column.
const stepsByFlow = groupBy('steps', 'flow_id');
const stepsByMasterStep = groupBy('steps', 'master_step_id');

const columnSteps = flow => {
  const columnSteps = [];
  const state = store.getState();
  const substepsOf = stepsByMasterStep(state);
  const steps = sortBy(stepsByFlow(state)[flow.id] || [], n => n.name);
  steps.forEach(step => {
    if (step.master_step_id === null) {
      const substeps = substepsOf[step.id] || [];
      if (substeps.length > 0) {
        substeps.forEach(substep => columnSteps.push(substep));
      } else {
        columnSteps.push(step);
      }
    }
  });
  return columnSteps;
};

const rowGenerator = (data, flow, stepsRMs) => {
  const cSteps = columnSteps(flow);
  const rows = [];

  data.forEach(mp => {
    const stepsRM = stepsRMs
      .filter(srm => srm.raw_material_id === mp.id)
      .map(s => s.step_id);
    if (stepsRM.length > 0) {
      // Only put substances where there is some intersection between LCS and step for the FLOW
      //  Look at line where we iterate matchs -> ...cSteps.map(cs => stepsRM.includes(cs.id) ? 'x' : '-')
      if ( cSteps.some(cs => stepsRM.includes(cs.id)) ) {
        rows.push([
          {
            text: [
              { text: mp.code, color: theme.palette.primary.main },
              ' - ',
              mp.name
            ]
          },
          ...cSteps.map(cs => stepsRM.includes(cs.id) ? 'x' : '-')
        ]);
      }
    }
  });
  return rows;
};

const infoGenerator = (flow) => {
  const preTableInfo = [];
  preTableInfo.push({ style: 'header_small', text: 'Legenda etapas:' });

  const state = store.getState();
  const substepsOf = stepsByMasterStep(state);
  const steps = sortBy(stepsByFlow(state)[flow.id] || [], n => n.name);
  steps.forEach(step => {
    if (step.master_step_id === null) {
      preTableInfo.push({ style: 'list', text: `${step.name} - ${step.description}` });
      const substeps = substepsOf[step.id] || [];
      if (substeps.length > 0) {
        substeps.forEach(substep =>
          preTableInfo.push({ style: 'sublist', text: `${substep.name} - ${substep.description}` })
        );
      }
    }
  });
  return preTableInfo;
};

const dd = (data, flow, stepRM) => {

  // Filter bu descriptor Types M, F, IS
  const stepRM_F = stepRM.filter(srm => srm.descriptors && srm.descriptors.includes(F));
  const stepRM_M = stepRM.filter(srm => srm.descriptors && srm.descriptors.includes(M));
  const stepRM_IS = stepRM.filter(srm => srm.descriptors && srm.descriptors.includes(IS));

  const columnStepNames = columnSteps(flow).map(s => s.name);

  return ({
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [ 40, 60, 40, 40 ],

    footer: function (currentPage, pageCount) {
        return {
          text: `${shortDate(new Date())} - ${currentPage.toString()} de ${pageCount}`,
          alignment: 'right',
          relativePosition: { x: -15, y: 20 },
          fontSize: 7
        };
    },
    header: function (currentPage, pageCount, pageSize) {
      return [
        {
          image: newLogoReachOk64,
          width: 50,
          relativePosition: { x: 20, y: 10 }
        }
      ];
    },
    content: [
      {
        style: 'header',
        text: `R - Fluxogramas e Etapas: ${flow.name}` //title,
      },
      ... infoGenerator(flow),
      {
        style: 'header2',
        text: `LCS - ${lcsNames[F_ID]}`
      },
      {
        style: 'tableExample',
        layout: 'lightHorizontalLines',
        table: {
          headerRows: 2,
          widths: [150, ...Array(columnStepNames.length).fill('*')],
          body: [
            [
              { text: '' },
              { text: 'Etapas', colSpan: columnStepNames.length - 1, alignment: 'center' },
              ...Array(columnStepNames.length - 1).fill({})
            ],
            ['Matérias Primas', ...columnStepNames],
            ...rowGenerator(data, flow, stepRM_F)
          ]
        }
      },
      {
        style: 'header2',
        text: `LCS - ${lcsNames[M_ID]}`
      },
      {
        style: 'tableExample',
        layout: 'lightHorizontalLines',
        table: {
          headerRows: 2,
          widths: [150, ...Array(columnStepNames.length).fill('*')],
          body: [
            [
              { text: '' },
              { text: 'Etapas', colSpan: columnStepNames.length - 1, alignment: 'center' },
              ...Array(columnStepNames.length - 1).fill({})
            ],
            ['Matérias Primas', ...columnStepNames],
            ...rowGenerator(data, flow, stepRM_M)
          ]
        }
      },
      {
        style: 'header2',
        text: `LCS - ${lcsNames[IS_ID]}`
      },
      {
        style: 'tableExample',
        layout: 'lightHorizontalLines',
        table: {
          headerRows: 2,
          widths: [150, ...Array(columnStepNames.length).fill('*')],
          body: [
            [
              { text: '' },
              { text: 'Etapas', colSpan: columnStepNames.length - 1, alignment: 'center' },
              ...Array(columnStepNames.length - 1).fill({})
            ],
            ['Matérias Primas', ...columnStepNames],
            ...rowGenerator(data, flow, stepRM_IS)
          ]
        }
      }
    ],

    styles: {
      header: {
        color: theme.palette.primary.main,
        bold: true,
        fontSize: 16,
        alignment: 'center',
        marginTop: 15
      },
      header2: {
        color: theme.palette.primary.main,
        bold: true,
        fontSize: 12,
        alignment: 'center',
        marginTop: 15
      },
      header_small: {
        fontSize: 8,
        bold: true,
        alignment: 'left'
      },
      list: {
        fontSize: 8,
        alignment: 'left'
      },
      sublist: {
        fontSize: 8,
        alignment: 'left',
        marginLeft: 10
      },
      tableExample: {
        margin: [0, 15, 0, 0],
        fontSize: 8
      }
    }
  });
};

export default dd;
